import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
    MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    ToggleButton, Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AddIcon from '@mui/icons-material/Add';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RefreshIcon from '@mui/icons-material/Refresh';
import Axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { SocketContext } from '../../socket';
import { getAuthHeader } from '../../utils/authToken';
import { useVoice } from '../../voice/VoiceContext';
import { formatPhone, personName } from '../../voice/callFormat';
import StudentCardModal from '../attendance/StudentCardModal';
import MemoEditModal from '../students/moreInfo/MemoEditModal';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { useMissedCallsFeed } from './useMissedCallsFeed';
import { useCellLocks } from './useCellLocks';
import useOwnedSocket from './useOwnedSocket';
import {
    DEFAULT_FILTERS, claimTakeover, filtersToParams, formatDateTime, formatListDate, hasAssignedWho, newStudentWarning,
    paramsToFilters, uploadedDay, whoLines,
} from './missedCallFormat';
import MissedCallRow, { COLUMNS, STICKY_COLUMNS, stickyColumnSx } from './MissedCallRow';
import ImportDialog from './ImportDialog';
import AddRowsDialog from './AddRowsDialog';
import SmsDialog from './SmsDialog';
import HistoryDialog from './HistoryDialog';
import MissedTimeDialog from './MissedTimeDialog';
import RestoreDialog from './RestoreDialog';
import AttachCallDialog from './AttachCallDialog';
import WhoDialog from './WhoDialog';
import NewStudentDialog from './NewStudentDialog';

const FILTERS_KEY = 'missedCalls.filters';
const SEARCH_DEBOUNCE_MS = 400;

const readSavedFilters = () => {
    try { return { ...DEFAULT_FILTERS, ...JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}') }; } catch { return DEFAULT_FILTERS; }
};

const UPLOAD_PATH = '/missed-calls-upload';  // the phone's own screen, outside the dashboard layout
const PAGE_BOTTOM_GUTTER = 24;  // the page's own py: 3
const MIN_TABLE_HEIGHT = 240;

/** The nearest ancestor that scrolls — the layout's <main className="content">. null = the viewport. */
const scrollParent = (el) => {
    for (let node = el.parentElement; node; node = node.parentElement) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
    }
    return null;
};

/**
 * Where each of the first columns starts, so they can be pinned while the rest scrolls sideways. The columns
 * size themselves to their content, so the offsets are read off the header cells and re-read whenever they
 * change width (a wider Who as rows load, a window resize). Rounded up: a fraction of a pixel short would let
 * a sliver of a scrolled-away column show through the seam.
 */
const useStickyOffsets = (headRef) => {
    const [offsets, setOffsets] = useState([]);
    useLayoutEffect(() => {
        const head = headRef.current;
        if (!head) return undefined;
        const cells = () => Array.from(head.children).slice(0, STICKY_COLUMNS);
        const measure = () => {
            let x = 0;
            const next = cells().map((cell) => {
                const left = Math.ceil(x);
                x += cell.getBoundingClientRect().width;
                return left;
            });
            setOffsets((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
        };
        measure();
        window.addEventListener('resize', measure);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        if (observer) cells().forEach((cell) => observer.observe(cell));
        return () => { window.removeEventListener('resize', measure); if (observer) observer.disconnect(); };
    }, [headRef]);
    return offsets;
};

/**
 * The table box runs from wherever it starts down to the bottom of the page, so its height never depends
 * on how many rows are loaded. `topRef` is the block above it — the only thing that moves the table down.
 */
const useFillHeight = (boxRef, topRef) => {
    const [height, setHeight] = useState(null);
    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return undefined;
        const measure = () => {
            const scroller = scrollParent(el);
            const pageHeight = scroller ? scroller.clientHeight : window.innerHeight;
            const rect = el.getBoundingClientRect();
            const top = scroller ? rect.top - scroller.getBoundingClientRect().top + scroller.scrollTop : rect.top + window.scrollY;
            const next = Math.max(MIN_TABLE_HEIGHT, Math.round(pageHeight - top - PAGE_BOTTOM_GUTTER));
            setHeight((h) => (h === next ? h : next));
        };
        measure();
        window.addEventListener('resize', measure);
        // Only the block above the table is watched — watching the table's own box would feed back into itself.
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        if (observer && topRef.current) observer.observe(topRef.current);
        return () => { window.removeEventListener('resize', measure); if (observer) observer.disconnect(); };
    }, [boxRef, topRef]);
    return height;
};

/**
 * The Missed Calls page (docs/MISSED_CALLS_PLAN.md §6): every missed call, newest day first, loaded as you
 * scroll, with filters; screenshots in, callbacks through the softphone, texts, 담당자, 완료, the task badge, and
 * an edit history — all live for every admin over Socket.io. Opens with no filter on.
 */
const MissedCalls = () => {
    const socket = useContext(SocketContext);
    const voice = useVoice();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState(() => (searchParams.toString() ? paramsToFilters(searchParams) : readSavedFilters()));
    const [qDraft, setQDraft] = useState(filters.q);
    const [config, setConfig] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [importOpen, setImportOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [phoneLinkOpen, setPhoneLinkOpen] = useState(false);
    const [admins, setAdmins] = useState([]);      // every admin by name: 담당자 연결 for everyone, 담당 지정 for managers
    const [smsRow, setSmsRow] = useState(null);
    const [historyRow, setHistoryRow] = useState(null);
    const [whoRow, setWhoRow] = useState(null);
    const [newStudentRow, setNewStudentRow] = useState(null);
    const [newStudentConfirmRow, setNewStudentConfirmRow] = useState(null);   // 👤+ on a row that already names someone
    const [claimConfirm, setClaimConfirm] = useState(null);   // { rowId, adminId } — taking a row another admin holds
    const [memoEdit, setMemoEdit] = useState(null);   // { studentUuid, memo } — the call's memo, open in the editor
    const [deleteRow, setDeleteRow] = useState(null);
    const [restoreRow, setRestoreRow] = useState(null);
    const [missedTimeRow, setMissedTimeRow] = useState(null);
    const [attachCallRow, setAttachCallRow] = useState(null);
    const [studentCard, setStudentCard] = useState(null);
    const meId = voice?.me?.admin?.id ?? null;
    const feed = useMissedCallsFeed({ filters, meId });
    const { upsert, reload: reloadFeed } = feed;

    // This page owns the shared socket while it is open (the voice feeds and the softphone ride on it too), and
    // keeps its token fresh so an expired JWT cannot leave reconnection failing silently for ever.
    useOwnedSocket(socket, () => enqueueSnackbar(
        '실시간 업데이트가 끊겼습니다. 목록은 1분마다 새로고침됩니다.',
        { variant: 'warning' },
    ));

    useEffect(() => {
        missedCallsApi.config().then(setConfig).catch(() => {});
        // Tasks record who claimed them by full name — the same value the consultation card compares.
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/admins/current-user`, { headers: { Authorization: getAuthHeader() } })
            .then((res) => setCurrentUser(res.data))
            .catch(() => {});
    }, []);

    // Filters live in the URL (shareable) and are remembered per browser.
    useEffect(() => {
        const params = filtersToParams(filters);
        setSearchParams(params, { replace: true });
        try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch { /* private mode */ }
    }, [filters, setSearchParams]);

    useEffect(() => {
        const id = setTimeout(() => setFilters((f) => (f.q === qDraft ? f : { ...f, q: qDraft })), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [qDraft]);

    const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));

    // 날짜 · 번호 · Who are pinned; the header row says how wide they are.
    const headRef = useRef(null);
    const stickyLeft = useStickyOffsets(headRef);

    // The table box fills what is left of the page under the filters.
    const topRef = useRef(null);
    const paperRef = useRef(null);
    const tableHeight = useFillHeight(paperRef, topRef);

    // Infinite scroll: a sentinel under the table loads the next page.
    const scrollRef = useRef(null);
    const sentinelRef = useRef(null);
    const { hasMore, loading, loadMore } = feed;
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) loadMore();
        }, { root: scrollRef.current, rootMargin: '300px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loading, loadMore]);

    /** Run an API call that answers with { row }; merge the row, report errors. */
    const act = useCallback(async (fn, okMessage) => {
        try {
            const data = await fn();
            if (data?.row) upsert(data.row);
            if (okMessage) enqueueSnackbar(okMessage, { variant: 'success' });
            return data;
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
            return null;
        }
    }, [upsert, enqueueSnackbar]);

    /**
     * Save one cell. A 409 means another admin had it open — the only case the page asks before writing,
     * because the alternative is losing what they are typing. Every other error is reported as usual.
     */
    const onSave = useCallback(async (row, field, value) => {
        try {
            // Which tab saved, so the server frees only THIS tab's hold on the cell — the same admin may have
            // it open elsewhere, and freeing that too would tell every other admin the cell is free.
            const data = await missedCallsApi.update(row.id, { [field]: value, socket_id: socket?.id ?? null });
            if (data?.row) upsert(data.row);
            return data;
        } catch (err) {
            if (err?.response?.status === 409) {
                const holder = err.response.data?.locked?.[0]?.holder;
                const who = holder?.name || '다른 관리자';
                if (!window.confirm(`${who}가 이 칸을 입력 중입니다. 그래도 저장할까요? (두 내용 모두 기록에 남습니다)`)) return null;
                return act(() => missedCallsApi.update(row.id, { [field]: value, force: true, socket_id: socket?.id ?? null }));
            }
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
            return null;
        }
    }, [act, upsert, enqueueSnackbar, socket?.id]);
    /**
     * 복구 — put a soft-deleted row back. Confirms first, since it reappears on everyone's list, and names the
     * number so the person clicking knows what is coming back (it may already be on that day's list again).
     */
    const doRestore = useCallback(
        (row) => act(() => missedCallsApi.restore(row.id), `${uploadedDay(row)} #${row.daily_seq} 복구했습니다`),
        [act],
    );
    /** Correct the 부재 time, or set it back to 시간 모름 (null). The server keeps it on the row's own day. */
    const doMissedTime = useCallback(
        (row, missedTime) => act(() => missedCallsApi.setMissedTime(row.id, missedTime), '부재 시간을 바꿨습니다'),
        [act],
    );
    const onClaim = useCallback((row, adminId) => act(() => missedCallsApi.claim(row.id, adminId)), [act]);
    // Taking a row another admin holds asks first — 담당하기, or a manager choosing someone else from 담당 지정. Until
    // this admin's own id has loaded there is no telling "mine" from "theirs", so a held row always asks then.
    const requestClaim = useCallback((row, adminId) => {
        const target = adminId ?? meId;
        const takes = target == null ? Boolean(row.claimed_by) : Boolean(claimTakeover(row, target));
        if (takes) setClaimConfirm({ rowId: row.id, adminId: adminId ?? null });
        else onClaim(row, adminId);
    }, [meId, onClaim]);
    const onUnclaim = useCallback((row) => act(() => missedCallsApi.unclaim(row.id)), [act]);
    const onHandoff = useCallback((row, adminId) => act(() => missedCallsApi.setHandoff(row.id, adminId)), [act]);
    const onHandled = useCallback((row, value) => act(() => missedCallsApi.setHandled(row.id, value)), [act]);
    const onWhoColor = useCallback((row) => act(() => missedCallsApi.setWhoColor(row.id, !row.who_is_red)), [act]);
    const onTaskChanged = useCallback((row) => act(() => missedCallsApi.get(row.id)), [act]);

    // Only this icon links a call to the row (office decision 2026-09-11).
    const openDialer = voice?.openDialer;
    const onCall = useCallback((row) => {
        if (!openDialer) return;
        const students = row.who?.students || [];
        const teacher = row.who?.teacher || undefined;
        // A manual multi-student pick has an explicit order: first is primary, the rest get memo copies.
        // An automatic sibling match remains ambiguous until an admin chooses.
        const student = !teacher && (students.length === 1 || row.who?.source === 'manual') ? students[0] : undefined;
        openDialer({
            to_number: formatPhone(row.phone_number) || row.phone_number,
            student,
            also_students: student && row.who?.source === 'manual' ? students.slice(1) : undefined,
            teacher,
            contact_label: teacher ? 'teacher' : (student?.contact_label || undefined),
            missed_call_id: row.id,
            missed_label: `${uploadedDay(row)} #${row.daily_seq}`,
        });
    }, [openDialer]);

    /**
     * 메모 수정: the row only carries the memo's id, so fetch the student's memos and open the one the call wrote.
     * It can be gone — reassigning the call to another student deletes the old memo — so say so rather than open
     * an empty editor.
     */
    const onEditMemo = useCallback(async (row) => {
        const ref = row.connected_call?.memo;
        if (!ref?.id || !ref.student_uuid) return;
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${ref.student_uuid}/memos`, { headers: { Authorization: getAuthHeader() } });
            const memo = (Array.isArray(res.data) ? res.data : []).find((m) => m.id === ref.id);
            if (!memo) {
                enqueueSnackbar('이 통화의 메모를 찾을 수 없습니다 (삭제되었을 수 있습니다)', { variant: 'warning' });
                return;
            }
            setMemoEdit({ studentUuid: ref.student_uuid, memo, rowId: row.id });
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    const onOpenStudent = useCallback(async (student) => {
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/students/${student.uuid}`, { headers: { Authorization: getAuthHeader() } });
            setStudentCard(res.data);
        } catch (err) {
            enqueueSnackbar(errorMessage(err), { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    // StudentCardModal reports through alert setters; show them as snackbars.
    const alertText = useRef({ success: '', error: '' });
    const alertProps = useMemo(() => ({
        setSuccessAlertMessage: (m) => { alertText.current.success = m; },
        setSuccessAlertOpen: (v) => { if (v) enqueueSnackbar(alertText.current.success || '저장했습니다', { variant: 'success' }); },
        setErrorAlertMessage: (m) => { alertText.current.error = m; },
        setErrorAlertOpen: (v) => { if (v) enqueueSnackbar(alertText.current.error || '오류가 발생했습니다', { variant: 'error' }); },
    }), [enqueueSnackbar]);

    const onSmsSent = useCallback((res) => {
        if (res?.row) upsert(res.row);
        if (res?.sent) {
            enqueueSnackbar(res.redirected_to ? `개발 환경: ${formatPhone(res.redirected_to)}로 보냈습니다` : '문자를 보냈습니다', { variant: 'success' });
        } else if (res?.reason === 'dev') {
            enqueueSnackbar('개발 환경이라 문자를 보내지 않았습니다 (MISSED_CALLS_DEV_SMS_TO 없음)', { variant: 'info' });
        }
    }, [upsert, enqueueSnackbar]);

    const onImported = useCallback((out) => {
        (out?.rows || []).forEach(upsert);
        // Saved, but the answer came back without the rows (reading them back failed on the server): load the table again.
        if ((out?.created?.length || out?.merged?.length) && !(out?.rows || []).length) reloadFeed();
        const skipped = out?.skipped?.length || 0;
        // A past day's list (plan §3.9) sits below today's rows, often further down than what is loaded: say where the
        // rows went, with 보기 to filter the table to those days. Every day the save touched, not just the first
        // row's — one upload can cover several (plan §3.9), and a single-day 보기 would filter away rows the
        // snackbar had just announced.
        const days = [...new Set((out?.rows || []).map((r) => r.list_date).filter(Boolean))].sort();
        const past = days.some((d) => config?.today && d !== config.today);
        enqueueSnackbar(
            `${past ? `${days.map(formatListDate).join(', ')} 목록: ` : ''}${out?.created?.length || 0}개 추가, ${out?.merged?.length || 0}개 갱신${skipped ? `, ${skipped}개 무시` : ''}`,
            {
                variant: 'success',
                ...(past && {
                    autoHideDuration: 10000,
                    action: (key) => (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setFilters((f) => ({ ...f, from: days[0], to: days[days.length - 1], deleted: false }));
                                closeSnackbar(key);
                            }}
                        >
                            보기
                        </Button>
                    ),
                }),
            },
        );
    }, [upsert, reloadFeed, enqueueSnackbar, closeSnackbar, config?.today]);

    // Only a manager (VOICE_ADMIN_EMAILS) may put another admin on a row (담당 지정); 담당자 연결 is anyone's to set,
    // so every admin loads the names.
    const manager = Boolean(voice?.manager);
    const locks = useCellLocks(meId);
    useEffect(() => {
        const load = () => missedCallsApi.admins().then((d) => setAdmins(d.admins || [])).catch(() => {});
        load();
        // A manager changed who is listed (담당자 목록 in the permission dialog): every open page reloads the names.
        if (!socket) return undefined;
        socket.on('missedCalls.staff', load);
        return () => { socket.off('missedCalls.staff', load); };
    }, [socket]);

    const uploadUrl = `${window.location.origin}${UPLOAD_PATH}`;
    const copyUploadUrl = async () => {
        try {
            await navigator.clipboard.writeText(uploadUrl);
            enqueueSnackbar('주소를 복사했습니다', { variant: 'success' });
        } catch {
            enqueueSnackbar('주소를 직접 복사해 주세요', { variant: 'info' });
        }
    };

    // 새 학생 만들기 on a row that already names someone asks first: it can create a duplicate family, and when the
    // call has a memo, re-attaching the call to the new student deletes that memo and its tasks (newStudentWarning).
    const requestNewStudent = useCallback((row) => {
        if (hasAssignedWho(row)) setNewStudentConfirmRow(row);
        else setNewStudentRow(row);
    }, []);

    const confirmDelete = async () => {
        const row = deleteRow;
        setDeleteRow(null);
        await act(() => missedCallsApi.remove(row.id), `${uploadedDay(row)} #${row.daily_seq} 삭제했습니다`);
    };

    // 담당자 filter: me / nobody / anyone who already holds a loaded row.
    const owners = useMemo(() => {
        const byId = new Map();
        for (const r of feed.rows) if (r.claimed_by && r.claimed_by.id !== meId) byId.set(r.claimed_by.id, r.claimed_by);
        return [...byId.values()];
    }, [feed.rows, meId]);
    // The current 담당자 목록 plus anyone already present on a loaded row, so an older assignment stays filterable
    // even after a manager removes that admin from the menus.
    const handoffAdmins = useMemo(() => {
        const byId = new Map(admins.map((a) => [a.id, a]));
        for (const r of feed.rows) if (r.handoff_to) byId.set(r.handoff_to.id, r.handoff_to);
        return [...byId.values()].sort((a, b) => personName(a).localeCompare(personName(b)));
    }, [admins, feed.rows]);

    const canCall = Boolean(voice?.canCall);
    const callBusy = Boolean(voice?.busy);
    const textMax = config?.text_max || 5000;
    const historyLive = historyRow ? feed.rows.find((r) => r.id === historyRow.id) || historyRow : null;
    const whoLive = whoRow ? feed.rows.find((r) => r.id === whoRow.id) || whoRow : null;
    const confirmLive = newStudentConfirmRow ? feed.rows.find((r) => r.id === newStudentConfirmRow.id) || newStudentConfirmRow : null;
    // The row as it is now: if it was deleted or filtered away the dialog closes, and if the other admin let go meanwhile
    // claimInfo is null and confirming simply takes a free row.
    const claimLive = claimConfirm ? feed.rows.find((r) => r.id === claimConfirm.rowId) || null : null;
    const claimInfo = claimLive ? claimTakeover(claimLive, claimConfirm.adminId ?? meId ?? -1) : null;
    const claimFromName = claimInfo ? (claimInfo.from.first_name || personName(claimInfo.from)) : '';
    const claimToSelf = Boolean(claimConfirm) && (claimConfirm.adminId == null || claimConfirm.adminId === meId);
    const claimToName = claimConfirm
        ? (claimToSelf ? '나' : (personName(admins.find((a) => a.id === claimConfirm.adminId)) || '선택한 관리자'))
        : '';
    const smsLive = smsRow ? feed.rows.find((r) => r.id === smsRow.id) || smsRow : null;
    const missedTimeLive = missedTimeRow ? feed.rows.find((r) => r.id === missedTimeRow.id) || missedTimeRow : null;
    const restoreLive = restoreRow ? feed.rows.find((r) => r.id === restoreRow.id) || restoreRow : null;
    const attachLive = attachCallRow ? feed.rows.find((r) => r.id === attachCallRow.id) || attachCallRow : null;

    return (
        <Box sx={{ px: { xs: 1.5, md: 4 }, py: 3 }}>
            <Box ref={topRef}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700 }}>Missed Calls</Typography>
                        <Typography variant="body2" color="text.secondary">
                            부재중 전화 목록 — 빨간색: 안내 메일 보내야 함 · 파란색: 안내 메일 보냄 · 검은색: 해당 없음
                        </Typography>
                    </Box>
                    <Button startIcon={<RefreshIcon />} onClick={feed.reload}>새로고침</Button>
                    <Button startIcon={<PhoneIphoneIcon />} onClick={() => setPhoneLinkOpen(true)}>휴대폰으로 업로드</Button>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                        번호로 추가
                    </Button>
                    <Button variant="contained" startIcon={<CloudUploadOutlinedIcon />} onClick={() => setImportOpen(true)}>
                        스크린샷 업로드
                    </Button>
                </Box>

                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
                    <ToggleButton size="small" value="open" selected={filters.handled === 'false'} onChange={() => setFilter({ handled: filters.handled === 'false' ? '' : 'false' })}>
                        미처리만
                    </ToggleButton>
                    <TextField size="small" type="date" label="시작일" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilter({ from: e.target.value })} />
                    <TextField size="small" type="date" label="종료일" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilter({ to: e.target.value })} />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>담당자</InputLabel>
                        <Select label="담당자" value={filters.claimed_by} onChange={(e) => setFilter({ claimed_by: e.target.value })}>
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="me">나</MenuItem>
                            <MenuItem value="none">없음</MenuItem>
                            {owners.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.first_name || a.last_name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>담당자 연결</InputLabel>
                        <Select label="담당자 연결" value={filters.handoff_to} onChange={(e) => setFilter({ handoff_to: e.target.value })}>
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="me">나</MenuItem>
                            <MenuItem value="none">없음</MenuItem>
                            {handoffAdmins.filter((a) => a.id !== meId).map((a) => (
                                <MenuItem key={a.id} value={String(a.id)}>{personName(a) || `#${a.id}`}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Who</InputLabel>
                        <Select label="Who" value={filters.who} onChange={(e) => setFilter({ who: e.target.value })}>
                            <MenuItem value="">전체</MenuItem>
                            <MenuItem value="student">학생</MenuItem>
                            <MenuItem value="parent">학부모</MenuItem>
                            <MenuItem value="teacher">선생님</MenuItem>
                            <MenuItem value="unknown">모름</MenuItem>
                        </Select>
                    </FormControl>
                    <ToggleButton color="error" size="small" value="who-red" selected={filters.who_red} onChange={() => setFilter({ who_red: !filters.who_red })}>
                        빨간 Who만
                    </ToggleButton>
                    <TextField size="small" label="전화번호 / 내용 검색" value={qDraft} onChange={(e) => setQDraft(e.target.value)} sx={{ minWidth: 180 }} />
                    <ToggleButton size="small" value="deleted" selected={filters.deleted} onChange={() => setFilter({ deleted: !filters.deleted })}>
                        삭제된 항목
                    </ToggleButton>
                    {(filters.handled || filters.from || filters.to || filters.claimed_by || filters.handoff_to || filters.who || filters.who_red || filters.q || filters.deleted) && (
                        <Button size="small" onClick={() => { setQDraft(''); setFilters(DEFAULT_FILTERS); }}>필터 지우기</Button>
                    )}
                </Stack>

                {feed.error && <Alert severity="error" sx={{ mb: 2 }}>{feed.error}</Alert>}
                {filters.deleted && <Alert severity="info" sx={{ mb: 2 }}>삭제된 항목입니다. 누가 언제 삭제했는지 표시되며, 복구할 수 있습니다.</Alert>}
            </Box>

            <Paper ref={paperRef} sx={{ width: '100%', overflow: 'hidden', boxShadow: 3 }}>
                <TableContainer ref={scrollRef} sx={{ height: tableHeight ?? 'calc(100vh - 260px)' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow ref={headRef}>
                                {COLUMNS.map((c, i) => (
                                    <TableCell
                                        key={c.key}
                                        sx={{
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            minWidth: c.minWidth,
                                            ...(i < STICKY_COLUMNS && stickyColumnSx({ left: stickyLeft[i], last: i === STICKY_COLUMNS - 1, head: true })),
                                        }}
                                    >
                                        {c.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feed.rows.map((row) => (
                                <MissedCallRow
                                    key={row.id}
                                    row={row}
                                    stale={feed.stale.has(row.id)}
                                    stickyLeft={stickyLeft}
                                    manager={manager}
                                    admins={admins}
                                    meId={meId}
                                    currentUser={currentUser}
                                    canCall={canCall}
                                    callBusy={callBusy}
                                    textMax={textMax}
                                    onCall={onCall}
                                    onSms={setSmsRow}
                                    onHistory={setHistoryRow}
                                    onDelete={setDeleteRow}
                                    onRestore={setRestoreRow}
                                    onMissedTime={setMissedTimeRow}
                                    onAttachCall={setAttachCallRow}
                                    onClaim={requestClaim}
                                    onUnclaim={onUnclaim}
                                    onHandoff={onHandoff}
                                    onHandled={onHandled}
                                    onSave={onSave}
                                    onOpenStudent={onOpenStudent}
                                    onTaskChanged={onTaskChanged}
                                    onWho={setWhoRow}
                                    onWhoColor={onWhoColor}
                                    onNewStudent={requestNewStudent}
                                    onEditMemo={onEditMemo}
                                    locks={locks}
                                />
                            ))}
                            <TableRow>
                                <TableCell colSpan={COLUMNS.length} sx={{ border: 0, p: 0 }}>
                                    <div ref={sentinelRef} style={{ height: 1 }} />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    {feed.loading && <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
                    {!feed.loading && feed.rows.length === 0 && (
                        <Typography sx={{ p: 3 }} color="text.secondary">
                            {filters.deleted ? '삭제된 항목이 없습니다.' : '부재중 전화가 없습니다. 오늘의 최근 통화 스크린샷을 올려 주세요.'}
                        </Typography>
                    )}
                </TableContainer>
            </Paper>

            <Dialog open={phoneLinkOpen} onClose={() => setPhoneLinkOpen(false)} fullWidth maxWidth="xs" disableEnforceFocus>
                <DialogTitle>휴대폰으로 업로드</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        사무실 iPhone의 Safari에서 이 주소를 열고 관리자 계정으로 로그인하면, 최근 통화 스크린샷을 바로 올릴 수 있습니다.
                        공유 → 홈 화면에 추가를 해 두면 앱처럼 열립니다.
                    </Typography>
                    <TextField
                        value={uploadUrl}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                        onFocus={(e) => e.target.select()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPhoneLinkOpen(false)}>닫기</Button>
                    <Button variant="contained" onClick={copyUploadUrl}>링크 복사</Button>
                </DialogActions>
            </Dialog>

            <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} config={config} onSaved={onImported} />
            <AddRowsDialog open={addOpen} onClose={() => setAddOpen(false)} config={config} onSaved={onImported} />
            <SmsDialog
                row={smsLive}
                open={Boolean(smsRow)}
                onClose={() => setSmsRow(null)}
                maxLength={config?.sms_max || 500}
                onSent={onSmsSent}
            />
            <HistoryDialog row={historyLive} open={Boolean(historyRow)} onClose={() => setHistoryRow(null)} />
            <MissedTimeDialog
                row={missedTimeLive}
                open={Boolean(missedTimeRow)}
                onClose={() => setMissedTimeRow(null)}
                onSave={doMissedTime}
            />
            <RestoreDialog
                row={restoreLive}
                open={Boolean(restoreRow)}
                onClose={() => setRestoreRow(null)}
                onRestore={doRestore}
            />
            <AttachCallDialog
                row={attachLive}
                open={Boolean(attachCallRow)}
                onClose={() => setAttachCallRow(null)}
                onAttached={(out) => {
                    if (out?.row) upsert(out.row);
                    enqueueSnackbar(out?.attached_student ? '통화를 연결하고 학생카드에도 기록했습니다' : '통화를 연결했습니다', { variant: 'success' });
                }}
            />
            <WhoDialog row={whoLive} open={Boolean(whoRow)} onClose={() => setWhoRow(null)} onSaved={upsert} />
            <NewStudentDialog row={newStudentRow} open={Boolean(newStudentRow)} onClose={() => setNewStudentRow(null)} onSaved={upsert} />
            {memoEdit && (
                <MemoEditModal
                    open
                    onClose={() => setMemoEdit(null)}
                    memo={memoEdit.memo}
                    studentUuid={memoEdit.studentUuid}
                    onSaved={() => {
                        enqueueSnackbar('메모를 저장했습니다', { variant: 'success' });
                        // The hooks push the refreshed row to every page; fetch it here too in case this socket is down.
                        missedCallsApi.get(memoEdit.rowId).then((d) => { if (d?.row) upsert(d.row); }).catch(() => {});
                    }}
                    onError={(err) => enqueueSnackbar(err?.response?.data?.message || errorMessage(err), { variant: 'error' })}
                />
            )}

            <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} disableEnforceFocus>
                <DialogTitle>이 행을 삭제할까요?</DialogTitle>
                <DialogContent>
                    {deleteRow && (
                        <Typography variant="body2">
                            {uploadedDay(deleteRow)} #{deleteRow.daily_seq} · {formatPhone(deleteRow.phone_number)} · {whoLines(deleteRow).map((l) => l.text).join(', ')}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">삭제된 행은 "삭제된 항목"에서 누가 언제 삭제했는지와 함께 볼 수 있습니다.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>취소</Button>
                    <Button color="error" variant="contained" onClick={confirmDelete}>삭제</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirmLive)} onClose={() => setNewStudentConfirmRow(null)} disableEnforceFocus>
                <DialogTitle>이미 지정된 사람이 있습니다</DialogTitle>
                <DialogContent>
                    {confirmLive && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {uploadedDay(confirmLive)} #{confirmLive.daily_seq} · {formatPhone(confirmLive.phone_number)}
                            </Typography>
                            {whoLines(confirmLive).map((l) => (
                                <Typography key={l.text} variant="body2" sx={{ fontWeight: 600 }}>{l.text}</Typography>
                            ))}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                기존 학생으로 바꾸려면 Who 칸의 검색 아이콘(Who 지정)을 쓰세요. 새 학생을 만들면 Who가 새 학생으로 바뀝니다.
                            </Typography>
                            {newStudentWarning(confirmLive) && (
                                <Alert severity="warning" sx={{ mt: 1.5 }}>{newStudentWarning(confirmLive)}</Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewStudentConfirmRow(null)}>취소</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={() => { setNewStudentRow(confirmLive); setNewStudentConfirmRow(null); }}
                    >
                        그래도 새 학생 만들기
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(claimLive)} onClose={() => setClaimConfirm(null)} disableEnforceFocus>
                <DialogTitle>이미 담당자가 있습니다</DialogTitle>
                <DialogContent>
                    {claimLive && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {uploadedDay(claimLive)} #{claimLive.daily_seq} · {formatPhone(claimLive.phone_number)}
                            </Typography>
                            {claimInfo ? (
                                <>
                                    <Typography variant="body2">
                                        현재 담당자: <b>{claimFromName}</b>{claimInfo.since ? ` (${formatDateTime(claimInfo.since)}부터)` : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        담당자 변경: {claimFromName} → {claimToName}. 변경 내역은 기록에 남습니다.
                                    </Typography>
                                    {claimInfo.onCall && (
                                        <Alert severity="warning" sx={{ mt: 1.5 }}>{claimFromName}님이 지금 이 행에서 통화 중입니다.</Alert>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body2">방금 담당자가 풀렸습니다. 그대로 진행할 수 있습니다.</Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClaimConfirm(null)}>취소</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        onClick={() => { const c = claimConfirm; setClaimConfirm(null); onClaim(claimLive, c.adminId ?? undefined); }}
                    >
                        {claimToSelf ? '담당 가져오기' : '담당 변경'}
                    </Button>
                </DialogActions>
            </Dialog>

            {studentCard && (
                <StudentCardModal
                    student={studentCard}
                    modalOpen={Boolean(studentCard)}
                    setModalOpen={(open) => { if (!open) setStudentCard(null); }}
                    setStudentUpdated={() => {}}
                    {...alertProps}
                />
            )}
        </Box>
    );
};

export default MissedCalls;
